import InputTextBox from "../Common/InputTextBox";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { Add } from '@mui/icons-material';
import { IconButton } from "@mui/material";
import { DataViewConfig } from "../../types/dataViews";
import { useEffect, useState } from "react";
import { DragIcon, WorkflowIcon } from "../../svgUrls";
import AntSwitchWithTick from "../Common/AntSwitchWithTick";
import { FallbackSvg } from '../Common/FallbackSvg';
import { WorkflowServiceIcon } from "../Common/WorkflowServiceIcon";


type DataViewsCustomizeViewProps = {
    columnVisibility: Record<string, boolean>;
    setColumnVisibility: (visibility: Record<string, boolean>) => void;
    columnOrder: DataViewConfig[];
    nodeIdToService: Record<string, string>;
    onDataViewsUpdate: (_columnOrder: DataViewConfig[]) => void;
}

const DataViewsCustomizeView = ({
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    nodeIdToService,
    onDataViewsUpdate,
}: DataViewsCustomizeViewProps) => {
    const [showMore, setShowMore] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [filteredColumnOrder, setFilteredColumnOrder] = useState([...columnOrder]);

    const onDragEnd = (result: DropResult) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }
  
      const list = [...columnOrder];
      const [removed] = list.splice(result.source.index, 1);
      list.splice(result.destination.index, 0, removed);
  
      onDataViewsUpdate(list);
    };

    useEffect(() => {
        let _columnOrder = [...columnOrder];
        _columnOrder = _columnOrder.filter(column => column.value.toLowerCase().includes(searchText.toLowerCase()));
        setFilteredColumnOrder([..._columnOrder]);
    }, [columnOrder, searchText]);

    const handleHideAll = () => {
        const updatedVisibility = { ...columnVisibility };
        columnOrder.forEach(column => {
            const columnName = `${column.value}|${column.node_id || 'none'}|${nodeIdToService[column.node_id || ''] || 'none'}`;
            updatedVisibility[columnName] = false;
        });
        setColumnVisibility(updatedVisibility);

        const updatedColumnOrder: DataViewConfig[] = columnOrder.map(col => ({
            ...col,
            is_set: false,
        }));
        onDataViewsUpdate(updatedColumnOrder);
    };

    const handleShowAll = () => {
        const updatedVisibility = { ...columnVisibility };
        columnOrder.forEach(column => {
            const columnName = `${column.value}|${column.node_id || 'none'}|${nodeIdToService[column.node_id || ''] || 'none'}`;
            updatedVisibility[columnName] = true;
        });
        setColumnVisibility(updatedVisibility);

        const updatedColumnOrder: DataViewConfig[] = columnOrder.map(col => ({
            ...col,
            is_set: true,
        }));
        onDataViewsUpdate(updatedColumnOrder);
    };

    return (
        <div className="p-4 max-h-[80%] overflow-auto">
            <InputTextBox
                initialValue={searchText}
                setTextValue={setSearchText}
                size="small"
            />
            <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
                <Droppable droppableId="droppable_options">
                    {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        {
                            filteredColumnOrder
                            .slice(0, showMore ? 11 : undefined)
                            .map((column, index) => {
                                const optionName = column.value;
                                const columnName = `${column.value}|${column.node_id || 'none'}|${nodeIdToService[column.node_id || ''] || 'none'}`;
                                return (
                                    <Draggable
                                        key={(column.node_id || "none")+"|"+column.value}
                                        draggableId={(column.node_id || "none")+"|"+column.value}
                                        index={index}
                                    >
                                        {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className="flex items-center"
                                        >
                                            <AntSwitchWithTick
                                                checked={columnVisibility[columnName]}
                                                onChange={(isChecked) => {
                                                    setColumnVisibility({
                                                        ...columnVisibility,
                                                        [columnName]: isChecked,
                                                    });
                                                    const _columnOrder: DataViewConfig[] = columnOrder.map(col => {
                                                        const colName = `${col.value}|${col.node_id || 'none'}|${nodeIdToService[col.node_id || ''] || 'none'}`;
                                                        if(colName === columnName)
                                                            return ({
                                                                ...col,
                                                                is_set: isChecked,
                                                            });
                                                        return ({...col})
                                                    })
                                                    onDataViewsUpdate(_columnOrder);
                                                }}
                                            />
                                            {nodeIdToService[column.node_id || ''] ?
                                                <WorkflowServiceIcon
                                                    workflowType={nodeIdToService[column.node_id || ''] || ''}
                                                    alt={nodeIdToService[column.node_id || '']}
                                                    height={20}
                                                    width={20}
                                                />
                                                : <WorkflowIcon />
                                            }
                                            <span className="ml-1 mr-4 flex-1 text-xs">
                                                {optionName}
                                            </span>
                                            <IconButton
                                                {...provided.dragHandleProps}
                                                edge="end"
                                                disabled={filteredColumnOrder.length !== columnOrder.length}
                                            >
                                                <DragIcon />
                                            </IconButton>
                                        </div>
                                        )}
                                    </Draggable>
                                );
                            })
                        }
                        {provided.placeholder}
                    </div>
                    )}
                </Droppable>
            </DragDropContext>
            {showMore && filteredColumnOrder?.length > 11 ? (
                <div
                    className="py-3 text-xs mt-2 flex gap-2 cursor-pointer items-center text-greyishBlue border-t border-solid border-borderGrey"
                    onClick={() => setShowMore(false)}
                >
                    <Add className="!text-greyishBlue !text-sm" />
                    Show {columnOrder.length - 11} more fields
                </div>
            ) : null}
            <div className="flex justify-between mb-2">
                <div onClick={handleHideAll} className="mr-2 bg-backgroundGreyLight p-2 rounded-lg cursor-pointer ">
                    Hide All
                </div>
                <div onClick={handleShowAll} className="mr-2 bg-backgroundGreyLight p-2 rounded-lg cursor-pointer">
                    Show All
                </div>
            </div>
        </div>
    );
}

export default DataViewsCustomizeView;
